import type { ApiClient } from "~/api/client";
import { parseWhen } from "~/features/automation/parseWhen";
import type { DecisionRowModel, ReminderRowModel, TaskRowModel } from "~/features/taskize/rows";

/**
 * The Taskize commit — one function, four writes, in an order that matters.
 *
 * **Decisions commit first, and a failed prose write aborts the whole confirm** before a
 * single task is created. That ordering is not stylistic: the decision block is the only write
 * that can fail *partially visible*, and creating six task records that reference a decision
 * the document never got is worse than creating nothing.
 *
 * The prose write goes through `documents.sculpt` (the caller's `saveBody`), never through an
 * editor handle — slate's only sanctioned body write: the server picks the safe apply path for
 * a collaborative document, takes a revertible checkpoint, and answers a destructive-confirm
 * 409 with a token (§C5, upholding W6 D7). It also works with no editor mounted, which is what
 * a shell-level opener requires.
 *
 * ⚠ **Lost update**: the caller re-reads the markdown immediately before calling, but a
 * collaborator can still write between that read and the sculpt. The checkpoint makes it
 * recoverable, not impossible (§E2 R5). Recorded rather than papered over.
 */

export interface ConfirmDeps {
  api: ApiClient;
  documentId: string;
  /** Freshly re-read, immediately before the call. */
  markdown: string;
  /** On `v2` the decision block is not written at all — sculpt refuses that lane (§C5/D3). */
  lane: "v1" | "v2";
  saveBody: (markdown: string) => Promise<unknown>;
  /** For the localised due date inside a lead-time reminder's note. */
  locale?: string;
  now?: Date;
}

export interface ConfirmOutcome {
  tasksCreated: number;
  decisionsWritten: number;
  remindersSet: number;
  /** Keys that actually landed — the tray removes exactly these. */
  committedKeys: string[];
}

/** kms's three-rung ladder, unchanged: NL parse → Date.parse → tomorrow 09:00. */
export function resolveReminderWhen(whenHint: string | null, now = new Date()): Date {
  if (whenHint?.trim()) {
    const parsed = parseWhen(whenHint, now);
    if (parsed) {
      return parsed.date;
    }
    // ⚠ A date-only ISO string parses as **UTC** midnight, so east of Greenwich its LOCAL hour
    // is 03:00, not 00:00 — checking `getHours() === 0` silently stops coercing there. The
    // date-only case is therefore recognised by its shape and built in local time.
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(whenHint.trim());
    if (dateOnly) {
      const [year, month, day] = whenHint.trim().split("-").map(Number);
      return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1, 9, 0, 0, 0);
    }
    const stamp = Date.parse(whenHint);
    if (!Number.isNaN(stamp)) {
      const date = new Date(stamp);
      // Nobody means "remind me at midnight".
      if (date.getHours() === 0 && date.getMinutes() === 0) {
        date.setHours(9, 0, 0, 0);
      }
      return date;
    }
  }
  const fallback = new Date(now);
  fallback.setDate(fallback.getDate() + 1);
  fallback.setHours(9, 0, 0, 0);
  return fallback;
}

function decisionMeta(decision: DecisionRowModel): string {
  const parts = [decision.status, decision.decidedByHint, decision.dateHint].filter(
    (part): part is string => Boolean(part?.trim()),
  );
  return parts.length > 0 ? ` — ${parts.join(" · ")}` : "";
}

/**
 * Append the confirmed decisions as callouts under a `## Decisions` heading, creating the
 * heading only if the document does not already have one.
 *
 * The rendered shape is what `existingTitles()` scrapes back out, so a second Taskize run on
 * the same document drops what this wrote. The two are one contract; change either and the
 * dedupe test fails.
 */
export function decisionBlockMd(markdown: string, decisions: DecisionRowModel[]): string {
  if (decisions.length === 0) {
    return markdown;
  }
  const hasHeading = /^#{1,6}\s+decisions\s*$/im.test(markdown);
  const body = decisions
    .map((decision) => `> 📌 **Decision:** ${decision.statement}${decisionMeta(decision)}`)
    .join("\n\n");
  const base = markdown.replace(/\s+$/u, "");
  return hasHeading ? `${base}\n\n${body}\n` : `${base}\n\n## Decisions\n\n${body}\n`;
}

/** `datetime-local` is wall time. The server parses `AssumeUniversal`, so ALWAYS send a zone. */
function isoFromLocalInput(value: string): string | null {
  if (!value) {
    return null;
  }
  const stamp = Date.parse(value);
  return Number.isNaN(stamp) ? null : new Date(stamp).toISOString();
}

const TIME_ZONE =
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : "UTC";

export async function confirmTaskize(
  rows: {
    tasks: TaskRowModel[];
    decisions: DecisionRowModel[];
    reminders: ReminderRowModel[];
  },
  deps: ConfirmDeps,
): Promise<ConfirmOutcome> {
  const now = deps.now ?? new Date();
  const committedKeys: string[] = [];

  // 1 — decisions, into the prose. First, and fatal: a failed write aborts everything.
  const decisions = deps.lane === "v2" ? [] : rows.decisions;
  if (decisions.length > 0) {
    await deps.saveBody(decisionBlockMd(deps.markdown, decisions));
    committedKeys.push(...decisions.map((decision) => decision.key));
  }

  // 2 — tasks, as durable records. Prose is NOT touched: the `## Tasks` checklist branch kms
  // still carried was unreachable dead code from before `DocumentTask` existed (§A2.4/D8).
  let tasksCreated = 0;
  let remindersSet = 0;
  for (const task of rows.tasks) {
    const dueIso = isoFromLocalInput(task.due);
    try {
      const created = await deps.api.documentTasks.create({
        documentId: deps.documentId,
        title: task.title,
        ...(task.details ? { details: task.details } : {}),
        ...(task.assigneeId ? { assigneeId: task.assigneeId } : {}),
        ...(dueIso ? { dueDate: dueIso } : {}),
        ...(task.sourceQuote ? { sourceQuote: task.sourceQuote } : {}),
      });
      tasksCreated += 1;
      committedKeys.push(task.key);

      // 3 — the lead-time reminder rides the task it belongs to, so cancelling one leaves a
      // trail back to the other.
      if (task.remindLeadMinutes !== null && dueIso) {
        const when = new Date(Date.parse(dueIso) - task.remindLeadMinutes * 60_000);
        const dueLabel = new Date(dueIso).toLocaleString(deps.locale);
        try {
          await deps.api.nudges.create(
            { targetType: "task", documentId: deps.documentId, recordId: created.id },
            {
              whenUtc: when.toISOString(),
              timezone: TIME_ZONE,
              note: `${task.title} — due ${dueLabel}`,
            },
          );
          remindersSet += 1;
        } catch {
          // The task is real either way; a failed reminder must not undo it.
        }
      }
    } catch {
      // Individual failures are skipped: one refused row must not cost the other five.
    }
  }

  // 4 — reminders, fire-and-forget.
  for (const reminder of rows.reminders) {
    try {
      await deps.api.nudges.create(
        { targetType: "document", documentId: deps.documentId },
        {
          whenUtc: resolveReminderWhen(reminder.whenHint, now).toISOString(),
          timezone: TIME_ZONE,
          note: reminder.title,
        },
      );
      remindersSet += 1;
      committedKeys.push(reminder.key);
    } catch {
      // Same rule as tasks.
    }
  }

  return {
    tasksCreated,
    decisionsWritten: decisions.length,
    remindersSet,
    committedKeys,
  };
}
